'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Offer } from 'shared/src/types/product.type';
import { useLiveProducts, type LiveProductFields } from '@/hooks/use-live-products';

/**
 * Plain, fully-serialisable per-product summary computed on the server and
 * handed across the RSC boundary. Crucially, this contains NO class instances
 * (Firestore Timestamp, etc.), so it can be passed straight into this client
 * component as a prop. We keep it minimal — only the bits needed to decide
 * whether attaching a listener is worth it AND to seed the per-product
 * freshness baseline. SSR continues to render the actual product fields.
 */
export type InitialProductBaseline = {
  title: string;
  /** Epoch seconds of the SSR `lastUpdated` for this product (0 if missing). */
  seconds: number;
  hasImages: boolean;
  hasExternalLink: boolean;
  /**
   * The key this product's offers are filed under ('asin:B0…' / 'name:…').
   * Optional: without it the offers listener has no way to attribute an
   * incoming `product_offers` row to a product on this page, so it stays off.
   */
  offerGroupKey?: string;
  /** The SSR offer list, so a single-row live change merges INTO what the
   *  reader is already looking at. Plain objects only — `verifiedAt` is an ISO
   *  string on purpose, precisely so this crosses the RSC boundary. */
  offers?: Offer[];
  /** The product's brand, so the client re-rank applies the SAME pick ladder as
   *  SSR (rung 2 is the brand's own store). Without it a live offer arrival
   *  could seat a different "Our pick" than a reload — the exact divergence the
   *  shared ranker exists to prevent. */
  brandName?: string;
  imageRevision?: number;
  /** True when the accepted cutout is due for a monthly refresh this visit. */
  imageRefreshDue?: boolean;
  /**
   * The page's own product (a product search's first product, `searchedProduct`):
   * the live identity guard judges its listing in brandOnly mode, as the server did.
   */
  searchedProduct?: boolean;
};

/**
 * Heuristic: products that were refreshed within this window cannot be eligible
 * for another background refresh in the next page-view, so a listener attached
 * for that visit can never deliver anything useful. Mirrors the `twelveHoursAgo`
 * gate in `getLinksPriceAndImages` for ASIN products (the most common path).
 */
const FRESH_WINDOW_MS = 12 * 60 * 60 * 1000;

/**
 * Heuristic: how long a freeze (set on buy intent) lasts. Long enough to cover
 * the user's hover + click + redirect to the partner retailer, short enough
 * that a stale freeze from minutes ago doesn't permanently block live updates
 * if the user navigates away and comes back to the same React tree.
 */
const FREEZE_DURATION_MS = 30 * 1000;

type LiveProductsContextValue = {
  /** Live overrides keyed by product `title`. Frozen products are filtered out. */
  overrides: Map<string, LiveProductFields>;
  /** True when this category's docPath was provided AND the listener is actually attached. */
  enabled: boolean;
  /**
   * Pin a product's currently-displayed values for ~30s. Called from the buy
   * CTA on pointer-enter / click so a price/link snapshot landing mid-checkout
   * cannot swap underneath the user. No-op when the provider is inactive.
   */
  freezeProduct: (title: string) => void;
};

const EMPTY_OVERRIDES: Map<string, LiveProductFields> = new Map();

const LiveProductsContext = createContext<LiveProductsContextValue>({
  overrides: EMPTY_OVERRIDES,
  enabled: false,
  freezeProduct: () => {},
});

type ProviderProps = {
  /** Firestore document path (e.g. `lang/en-US/categories/...`). When omitted the provider is a no-op pass-through. */
  docPath?: string;
  /**
   * Plain, pre-serialised per-product summary computed on the server.
   * Used to seed the freshness baseline AND decide whether attaching a
   * listener is worth it at all. MUST stay primitives-only — passing class
   * instances (Firestore Timestamp etc.) directly across the RSC boundary
   * triggers React's "Only plain objects… can be passed to Client Components"
   * error.
   */
  initialBaseline?: InitialProductBaseline[];
  /**
   * Skip listener attach for bot user-agents. Bots never observe live updates
   * (they read SSR HTML once and leave) so the Firestore reads would be pure
   * waste. Pass the same `isBotRequest` value the page already computes.
   */
  isBot?: boolean;
  /**
   * The page's MARKET ('CA', 'UK', …) — URL-derived, never IP geo. Supplied
   * together with `offerGroupKey`s on the baseline, it turns on the
   * `product_offers` listener so offers a human visit just paid for appear in
   * the panel seconds later. Omitted, nothing changes: the offers a reader sees
   * are the SSR ones until the next navigation.
   */
  offersRegionCode?: string;
  /**
   * Attach the listener even when SSR products look complete. Used for a
   * first-party search landing on an existing page so delayed offer/link/image
   * writes still paint without a reload. Do not set this from ordinary visits.
   */
  forceAttach?: boolean;
  children: ReactNode;
};

/**
 * Are *any* of the SSR products eligible for a live update in this visit?
 *
 * A product is "stuck on stale" if either:
 *   - its `lastUpdated` is older than the 12h freshness window
 *     (background refresh will fire on this visit and may write fresh data)
 *   - it is missing images OR an external link entirely
 *     (the refresh path always tries to fill these regardless of timestamp)
 *
 * If every product is fresh AND fully populated, the listener cannot deliver
 * anything user-visible during this visit, so we don't open the connection.
 */
function hasAnyStaleProduct(baseline: InitialProductBaseline[] | undefined): boolean {
  if (!baseline || baseline.length === 0) return false;
  const cutoffSeconds = Math.floor((Date.now() - FRESH_WINDOW_MS) / 1000);
  for (const p of baseline) {
    if (!p.seconds || p.seconds < cutoffSeconds) return true;
    if (!p.hasImages) return true;
    if (!p.hasExternalLink) return true;
    if (p.imageRefreshDue) return true;
  }
  return false;
}

/**
 * Subscribes to a single category Firestore doc and exposes per-product live
 * overrides via React context. Wraps the rendered product tree so any cell can
 * call `useLiveProduct(productTitle)` and get the freshest available value
 * without re-rendering siblings that didn't change.
 *
 * Cost-control gates (see `hasAnyStaleProduct` and `isBot`):
 *   - bots never see live updates → don't open a listener for them.
 *   - pages whose products are all fresh + fully populated cannot benefit from
 *     a listener during this visit → don't open one. This typically eliminates
 *     the vast majority of listener attachments site-wide.
 *
 * Buy-intent freeze (see `freezeProduct`):
 *   - the buy CTA marks its product on pointer-enter / click; while frozen,
 *     incoming snapshots are masked so prices/links can't swap mid-checkout.
 *
 * SEO / AI note (deliberate divergence — DO NOT "fix"):
 *   The page's JSON-LD `ProductSchema` blocks are server-rendered from the
 *   SSR snapshot and intentionally NOT updated by this provider. Bots that
 *   don't run JS see consistent SSR values; the next ISR rebuild
 *   (`revalidatePath` after the background write) re-converges JSON-LD with
 *   the live DOM. Mirroring live updates into JSON-LD via injected
 *   `<script type="application/ld+json">` would be flagged as content
 *   instability by Google and gain us nothing — the visible price + microdata
 *   `<meta itemProp="price">` already update in the same render and are what
 *   modern AI/parsers cite. Keep this asymmetry on purpose.
 */
export function LiveProductsProvider({
  docPath,
  initialBaseline,
  isBot,
  offersRegionCode,
  forceAttach,
  children,
}: ProviderProps) {
  // Decide ONCE per mount whether the listener is worth attaching. Recomputing
  // on every render would re-derive an effective docPath (and thrash the
  // listener) when React re-renders for unrelated reasons.
  const [shouldAttach] = useState(() => {
    if (!docPath) return false;
    if (isBot) return false;
    return Boolean(forceAttach) || hasAnyStaleProduct(initialBaseline);
  });

  const effectiveDocPath = shouldAttach ? docPath : undefined;
  const liveOverrides = useLiveProducts(effectiveDocPath, initialBaseline, offersRegionCode);

  // Per-product freeze timestamps. When a CTA signals buy intent we pin the
  // current displayed values so an incoming snapshot can't swap underneath
  // the user. Stored in a ref so toggling doesn't re-render the whole tree;
  // we bump a version state to flush filtered overrides downstream.
  const freezesRef = useRef<Map<string, number>>(new Map());
  const [freezeVersion, setFreezeVersion] = useState(0);

  const freezeProduct = useCallback((title: string) => {
    if (!title) return;
    freezesRef.current.set(title, Date.now() + FREEZE_DURATION_MS);
    setFreezeVersion((v) => v + 1);
  }, []);

  // Mask frozen products' overrides so consumers fall back to SSR values.
  // Cheap to recompute: only runs when liveOverrides reference or freezeVersion change.
  // This is also the ONLY place the buy-intent pin is enforced for `offers`:
  // deleting the whole entry pins the offer list the reader is choosing from,
  // so a panel cannot re-order under a cursor that is already on a row.
  const overrides = useMemo<Map<string, LiveProductFields>>(() => {
    if (freezesRef.current.size === 0) return liveOverrides;
    const now = Date.now();
    const next = new Map(liveOverrides);
    for (const [title, until] of freezesRef.current.entries()) {
      if (until > now) {
        next.delete(title);
      } else {
        freezesRef.current.delete(title);
      }
    }
    return next;
  }, [liveOverrides, freezeVersion]);

  const value = useMemo<LiveProductsContextValue>(
    () => ({ overrides, enabled: shouldAttach, freezeProduct }),
    [overrides, shouldAttach, freezeProduct],
  );

  return <LiveProductsContext.Provider value={value}>{children}</LiveProductsContext.Provider>;
}

/**
 * Returns live overrides for a single product (by title), or undefined when:
 *   - the provider is not mounted (e.g. rendered outside a category page),
 *   - no docPath was supplied,
 *   - the listener was gated off (bot, or all products fresh + complete),
 *   - the product is currently frozen by buy intent,
 *   - or no fresher snapshot has arrived yet.
 *
 * Consumers should fall back to their SSR product fields:
 *   const live = useLiveProduct(product.title);
 *   const price = live?.price ?? product.price;
 */
export function useLiveProduct(productTitle: string | undefined): LiveProductFields | undefined {
  const ctx = useContext(LiveProductsContext);
  if (!ctx.enabled || !productTitle) return undefined;
  return ctx.overrides.get(productTitle);
}

/**
 * Returns a stable callback that pins this product's displayed values for
 * ~30s, so a snapshot landing while the user is hovering / clicking the buy
 * CTA cannot swap the price or affiliate link underneath them. Safe to call
 * even when the provider is inactive (no-op).
 */
export function useFreezeProduct(): (title: string) => void {
  return useContext(LiveProductsContext).freezeProduct;
}

/**
 * Returns true for ~`durationMs` after the given dependency value changes,
 * skipping the very first render so the SSR-mounted state doesn't trigger
 * a flash. Wire `dep` to whatever uniquely identifies the live payload
 * (e.g. live?.lastUpdated?.seconds, or the swapped image URL) so the fade
 * fires exactly once per real value change.
 *
 * Designed for opacity transitions on live-overridden values: a quick
 * 150ms dip from full opacity to ~70% and back. Subtle enough to not look
 * like a glitch, deliberate enough that the user understands "this just
 * updated to a fresher value" rather than "this broke for a frame".
 */
export function useChangePulse(dep: unknown, durationMs = 250): boolean {
  const [pulsing, setPulsing] = useState(false);
  const isFirstRunRef = useRef(true);

  useEffect(() => {
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      return;
    }
    setPulsing(true);
    const timer = setTimeout(() => setPulsing(false), durationMs);
    return () => clearTimeout(timer);
  }, [dep, durationMs]);

  return pulsing;
}
